import { Anchor, Button, DescriptionValidator, FieldController, NameValidator, TextArea, TextField } from '@keplerco/core';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StepSkeleton } from '../../../../components/general/stepper/step/step.skeleton';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { PagePath } from '../../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../../overmind';
import { IDetailsStepProps } from './details-step.models';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { CreateSkillCategoryRequest, GetSkillCategoryResponse, UpdateSkillCategoryRequest } from '../../../../overmind/actions';

export function DetailsStep({ parentIsLoading, skillCategory, onNext }: IDetailsStepProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { control, setValue, handleSubmit } = useForm<any>({ mode: 'onBlur' });

  useEffect(() => {
    setValue('name', skillCategory?.name);
    setValue('description', skillCategory?.description);
  }, [skillCategory]);

  if (parentIsLoading || isLoading) return <StepSkeleton />;

  return (
    <form
      id="skill-category-details"
      style={{ width: '100%' }}
      onSubmit={handleSubmit(async values => {
        setIsLoading(true);

        const nextSkillCategory: GetSkillCategoryResponse = {
          slug: skillCategory?.slug ?? '',
          name: values.name,
          description: values.description,
          isArchived: skillCategory?.isArchived ?? false,
          skills: skillCategory?.skills ?? [],
        };

        if (!skillCategory) {
          const request: CreateSkillCategoryRequest = {
            name: values.name,
            description: values.description,
            skillSlugs: [],
          };
          const response = await actions.createSkillCategory(request);
          // TODO: error handling
          if (!response) return setIsLoading(false);
          nextSkillCategory.slug = response;
        } else {
          const request: UpdateSkillCategoryRequest = {
            skillCategorySlug: skillCategory.slug,
            name: values.name,
            description: values.description,
            skillSlugs: skillCategory.skills.map(skill => skill.slug),
          };
          await actions.updateSkillCategory(request);
        }

        onNext(nextSkillCategory);

        setIsLoading(false);
      })}
    >
      <SkeletonOverlay isLoading={parentIsLoading || isLoading}>
        <WidgetCard title="Details" subtitle="Name your skill category, this will be displayed in analytics">
          <FieldController
            name="name"
            control={control}
            rules={new NameValidator()}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="text"
                label="Name"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid,
                  message: fieldState.error?.message ?? 'Enter a name',
                }}
              />
            )}
          />

          <FieldController
            name="description"
            control={control}
            rules={new DescriptionValidator(false)}
            render={({ field, fieldState }) => (
              <TextArea
                {...field}
                label="Description"
                responsive
                validation={{
                  dirty: fieldState.isDirty || !!fieldState.error,
                  invalid: fieldState.invalid,
                  message: fieldState.error?.message ?? 'Enter a description',
                }}
              />
            )}
          />
        </WidgetCard>
      </SkeletonOverlay>

      <footer className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--background)', marginTop: 24 }}>
        <Anchor arrow reverse onClick={() => keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementSkillCategories.replace(':companySlug', companyVariables.slug!)}`)}>
          Cancel
        </Anchor>

        <Button type="button" arrow filled>
          Next
        </Button>
      </footer>
    </form>
  );
}
